import { ref, watch } from 'vue';

import { defineStore } from 'pinia';

import { useMessagesCall } from '@/composables/domains/chat/useMessagesCall';
import useWebSocketUrl from '@/composables/shared/useWebSocketUrl';
import { getNormalizedLanguageCode } from '@/static/languages.js';
import { useCallStore } from '@/stores/call';
import { useMicrophoneStore } from '@/stores/microphone';
import {
  Attendee,
  ConnectionInfo,
  IMessageEp,
  IMessageFP,
  IMessageMf,
  IMessageVc,
  IMessageVd,
  IMessageVr,
  MessageFlag,
  InterpretationSpeed,
  SocketCloseReasonCode,
} from '@/types/socket';
import { OutboundMessageDM, OutboundMessageEM } from '@/types/socket/outbound';
import convertASCII from '@/utils/convertASCII';
import { isInCapacitorApp } from '@/utils/device';
import { base64UrlSafeToString } from '@/utils/encryptor.js';

export const useSocketStore = defineStore('socket', () => {
  const isMobile = isInCapacitorApp();
  const socketBaseURL = import.meta.env.VITE_SOCKET_BASE_URL || 'wss://dev-soma.sconai.com/ws';

  const callStore = useCallStore();
  const microphoneStore = useMicrophoneStore();

  const { requestWebsocketUrl, webSocketUrl } = useWebSocketUrl();

  const socket = ref<WebSocket>();
  const selectedUserId = ref<string>('');
  const isGuestMicMuted = ref<boolean>(false); //청자가 받은 mute 정보
  const muteSenderId = ref<string>(''); // 청자가 받은 mute 정보
  const isFastInterpretation = ref<boolean>(false); //빠르게 사운드 재생 ON/OFF정보

  //data
  const selectedNickname = ref<string>('');
  const selectedChatId = ref<string>('');
  const selectedIsVoiceRegistered = ref<'0' | '1'>('0');
  const selectedRegisteredDeviceId = ref<string>('0');
  const connectInfo = ref<ConnectionInfo | undefined>();

  const socketState = ref(SocketState.CLOSED);
  const recover = ref<boolean>(false); // 이미 connected 요청시 타임아웃 된 친구가 접속 시도했을 때
  const evicted = ref<boolean>(false); // 강퇴 당했을 때, 사유는 다양할 수 있음 (ReasonCode) / nd는 호스트가 다 나갔을 때 통역종료
  const endOfDemo = ref<boolean>(false); // 데모 10분 타임아웃시
  const remainingMinutes = ref(0); // 남은시간

  const attendees = ref<Set<Attendee>>(new Set());
  const exceededUsageLimit = ref<boolean>(false);

  const channel = ref<BroadcastChannel>(); // 동시접속 제한을 위해 사용
  const connectionResponse = ref(-1); // -1: 연결전, 0: 소켓연결중, 1: 소켓연결 성공, 2: CC 성공, 나머지 코드는 에러 코드, 9999: 접속실패(에러)
  const evictMySelf = ref<'0' | '1'>('0');
  const micAdvancedOptions = ref<IMessageEp>({
    upperThreshold: -25.0, // 상향값 (최대 -5 기본값 -25.0 최소 -60)
    lowerThresholdLead: -45.0, // 하향리드값 (최대 -5 기본값 -45.0 최소 -60)
    lowerThresholdTail: -45.0, // 하향테일값 (최대 -5 기본값 -45.0 최소 -60)
    vadThreshold: -45.0,
  });
  const interpretationSpeed = ref<InterpretationSpeed>('NORMAL');

  let streamingCheckCount = 0;

  let pingInterval;
  let pingTimeout;

  const setupWebSocket = async (forceReconnection = false) => {
    if (!connectInfo.value) {
      console.error('connectInfo is undefined');
      return;
    }

    if (socket.value && socket.value.readyState === WebSocket.OPEN) {
      console.log('Socket Already opened..');
      if (!forceReconnection) return;
    }
    if (socket.value && socket.value.readyState !== WebSocket.CLOSED) {
      console.log('Socket close socket..');
      socket.value.close();
    }

    socketState.value = SocketState.CONNECTING;
    console.log('socketState: CONNECTING');
    await requestWebsocketUrl(connectInfo.value);
    socket.value = new WebSocket(webSocketUrl.value);

    socket.value.onopen = () => {
      connectionResponse.value = 1;
      console.log('Connected to WebSocket');

      if (connectInfo.value?.connectionType !== 'VOICE-STREAMING') sendMessageConnect();

      socketState.value = SocketState.OPEN;

      // 5초 후부터 30초 간격으로 ping 전송
      pingTimeout = setTimeout(() => {
        sendMessagePI();
        pingInterval = setInterval(() => {
          sendMessagePI();
        }, 30000); // 30초 간격
      }, 5000); // 5초 후 시작
    };

    socket.value.onmessage = (event) => {
      if (connectInfo.value && ['GENERAL', 'VOICE-STREAMING'].includes(connectInfo.value.connectionType)) {
        handleWebSocketMessage(event.data);
      }
    };

    socket.value.onerror = (error) => {
      connectionResponse.value = 9999;

      console.error('WebSocket Error: ', error);
      if (socketState.value !== SocketState.RETRY_FAILED && socketState.value !== SocketState.CLOSED) {
        clearInterval(pingInterval);
        socketState.value = SocketState.CLOSED;
      }
      if (pingInterval) {
        clearInterval(pingInterval);
        pingInterval = null;
      }
      if (pingTimeout) {
        clearTimeout(pingTimeout);
        pingTimeout = null;
      }
    };
    //
    socket.value.onclose = (event) => {
      if (socketState.value !== SocketState.RETRY_FAILED && socketState.value !== SocketState.CLOSED) {
        clearTimeout(pingTimeout);
        clearInterval(pingInterval);
        socketState.value = SocketState.CLOSED;
      }
      if (pingInterval) {
        clearInterval(pingInterval);
        pingInterval = null;
      }
      if (pingTimeout) {
        clearTimeout(pingTimeout);
        pingTimeout = null;
      }
    };
  };

  const sendMessage = (payload) => {
    if (socket.value && socket.value.readyState === WebSocket.CONNECTING) {
      setTimeout(() => {
        sendMessage(payload);
      }, 500);
      return;
    }

    if (socket.value && socket.value.readyState === WebSocket.OPEN) {
      socket.value.send(payload);
    } else {
      console.error('WebSocket is not open:', socket.value ? socket.value.readyState : 'socket is null');
    }
  };

  //연결정보 저장
  const setConnectInfo = (connectInfoNew: ConnectionInfo) => {
    connectInfoNew.languageCode = getNormalizedLanguageCode(connectInfoNew.languageCode);
    connectInfo.value = connectInfoNew;
    const qrCodeType = callStore.qrCodeInfo.qrCodeType;

    if (!channel.value) {
      channel.value = new BroadcastChannel('connect_ws');
      channel.value.postMessage({
        type: 'connect_ws',
        value: selectedChatId.value + '|' + qrCodeType,
      });
    }
  };

  const sendMessageConnect = () => {
    console.trace('sendMessageConnect', connectInfo.value);
    const payload = buildPayload('cc', connectInfo.value);
    sendMessage(payload);
  };

  const sendMessageDisconnect = () => {
    const userId = connectInfo.value?.userId;
    const chatId = selectedChatId.value;
    const payload = buildPayload('cd', { userId, chatId });
    sendMessage(payload);
  };

  const sendMessageTss = () => {
    const payload = buildPayload('tt', {
      isTssOn: microphoneStore.isTssOn ? '1' : '0',
    });
    sendMessage(payload);
  };

  const sendMessageMuteGuests = (AllowMicOn) => {
    const payload = buildPayload('am', {
      isMuteOn: AllowMicOn ? '1' : '0',
      senderId: localStorage.getItem('userIdConnected'),
    });
    sendMessage(payload);
    isGuestMicMuted.value = AllowMicOn;
  };

  const sendMessageFi = (fastInterpretationOn) => {
    const payload = buildPayload('fi', {
      isFastInterpretationOn: fastInterpretationOn ? '1' : '0',
    });
    sendMessage(payload);
    isFastInterpretation.value = fastInterpretationOn;
  };

  const sendMessageIc = () => {
    const payload = buildPayload('ic');
    sendMessage(payload);
  };

  const sendMessageFc = (payloadRaw) => {
    const payload = buildPayload('fc', payloadRaw);
    sendMessage(payload);
  };

  const sendMessageAttendees = () => sendMessage(buildPayload('al'));

  const sendMessageEvictUser = (userId, chatId) => sendMessage(buildPayload('ev', { userId, chatId }));

  const sendMessageChangeDevice = (voiceId) => {
    voiceId = voiceId ?? '0';
    const longVoice = ['CONSULTATION_CALL', 'GENERAL_CALL_PARTNER', 'CONFERENCE_CALL_PARTNER'].includes(
      callStore.qrCodeInfo.qrCodeType
    );
    console.warn('CHANGE DEVICE', { voiceId, longVoice });

    return sendMessage(buildPayload('dv', { voiceId, longVoice }));
  };

  const sendMessageChangeLanguage = (languageCode: string, languageCodeAway?: string) => {
    console.warn('CL', languageCode);
    if (typeof languageCode !== 'string') {
      console.error('Invalid languageCode:', languageCode);
      return false;
    }
    languageCode = getNormalizedLanguageCode(languageCode);
    languageCodeAway = languageCodeAway ? getNormalizedLanguageCode(languageCodeAway) : null;

    try {
      sendMessage(buildPayload('cl', { languageCode, languageCodeAway }));
      localStorage.setItem('language', languageCode);
      return true;
    } catch {
      return false;
    }
  };

  const dispatchMessage = <T>(flag: MessageFlag, message?: T) => {
    sendMessage(buildPayload(flag, message));
  };

  const sendMessageAudioStreamStart = () => sendMessage(buildPayload('ah'));

  const sendMessageAudioStreamEnd = () => sendMessage(buildPayload('ac'));

  const sendMessageVoiceStreamStart = (message: IMessageVr) => sendMessage(buildPayload('vr', message));
  const sendMessageVoiceStreamEnd = (message: IMessageVc) => sendMessage(buildPayload('vc', message));

  const sendMessageText = (message: IMessageMf) => {
    sendMessage(buildPayload('mf', message));
  };

  const sendMessageVAD = (message: IMessageVd) => {
    sendMessage(buildPayload('vd', message));
  };

  const sendMessagePI = () => {
    const payload = buildPayload('pi');
    sendMessage(payload);
  };

  const sendMessageEp = (message: IMessageEp) => {
    const payload = buildPayload('ep', message);
    sendMessage(payload);
  };

  const sendMessageFP = (message: IMessageFP) => {
    const payload = buildPayload('fp', message);
    sendMessage(payload);
  };

  const sendMessageEM = (message: OutboundMessageEM) => {
    const payload = buildPayload('em', message);
    sendMessage(payload);
  };

  const sendMessageDM = (message: OutboundMessageDM) => {
    const payload = buildPayload('dm', message);
    sendMessage(payload);
  };

  const sendMessageAudioStreamChunk = (audioBytes, messageFlag: MessageFlag) => {
    streamingCheckCount = streamingCheckCount + 1;

    if (socket.value?.readyState !== WebSocket.OPEN) {
      console.error('[WEBSOCKET] WebSocket is not open. Current state:', socket.value?.readyState);
      socketState.value = SocketState.CLOSED;
      return;
    }

    const flag = convertASCII(messageFlag);
    const messageLength = audioBytes.byteLength.toString().padStart(8, '0');
    const payload = new Uint8Array([...flag, ...new TextEncoder().encode(messageLength), ...audioBytes]);
    sendMessage(payload);
  };

  const buildPayload = (flagStr, payloadObjOrStr: any = '') => {
    const flag = [...new TextEncoder().encode(flagStr)];
    const payloadStr = typeof payloadObjOrStr === 'string' ? payloadObjOrStr : JSON.stringify(payloadObjOrStr);
    const message = new TextEncoder().encode(payloadStr);
    const messageLength = payloadStr.length.toString().padStart(8, '0');
    return new Uint8Array([...flag, ...new TextEncoder().encode(messageLength), ...message]);
  };

  const getCloseReason = (): string => {
    const stack = new Error().stack?.split('\n').slice(4) ?? [];
    const encoded = new TextEncoder().encode(stack.slice(0, 2).join(' | ') || 'No stack trace');

    return new TextDecoder().decode(encoded.slice(0, 123));
  };

  const close = (code: SocketCloseReasonCode = 1000) => {
    if (socket.value && socket.value.readyState !== WebSocket.CLOSED) {
      socket.value.close(code, getCloseReason());
    }
  };

  const removeAttendeeByUserId = async (userId) => {
    if (localStorage.getItem('userId') === userId) {
      return;
    }
    attendees.value = new Set([...attendees.value].filter((attendee) => attendee.userId !== userId));
  };

  const removeAttendeeAllExcludeMine = () => {
    const userId = localStorage.getItem('userId');
    attendees.value = new Set([...attendees.value].filter((attendee) => attendee.userId === userId));
  };

  const addAttendeeByUserId = async (userId, nickname, isPartnerPerson) => {
    attendees.value.add({ userId, nickname, isPartnerPerson });
  };

  const findAttendeeNickname = (userId) => {
    for (const attendee of attendees.value) {
      if (attendee.userId === userId) {
        return attendee.nickname;
      }
    }
    return '';
  };

  const findAttendee = (userId) => {
    for (const attendee of attendees.value) {
      if (attendee.userId === userId) {
        return attendee;
      }
    }
    return '';
  };

  // 참여자 목록 초기화
  const resetAttendees = () => {
    attendees.value = new Set();
  };

  const setAttendees = (ats) => {
    attendees.value = new Set(
      ats.map((it) => ({
        userId: it.userId,
        nickname: base64UrlSafeToString(it.nickname),
        isPartnerPerson: it.isPartnerPerson,
      }))
    );
  };

  // 초과 여부
  const setExceededUsageLimit = (val: boolean) => {
    exceededUsageLimit.value = val;
  };

  // 남은 시간
  const setRemainingMinutes = (minutes: number) => {
    remainingMinutes.value = minutes;
  };

  // 통역 속도
  const setInterpretationSpeed = (type: InterpretationSpeed) => {
    interpretationSpeed.value = type;
  };

  watch(
    () => microphoneStore.isTssOn,
    () => sendMessageTss()
  );

  const { handleWebSocketMessage } = useMessagesCall({
    isGuestMicMuted,
    muteSenderId,
    attendees,
    exceededUsageLimit,
    recover,
    evicted,
    isFastInterpretation,
    selectedChatId,
    connectionResponse,
    socketState,
    micAdvancedOptions,
    sendMessageConnect,
    setAttendees,
    findAttendeeNickname,
    addAttendeeByUserId,
    removeAttendeeByUserId,
    sendMessageAttendees,
  });

  return {
    socket,
    muteSenderId,
    isGuestMicMuted,
    isFastInterpretation,
    selectedUserId,
    selectedNickname,
    selectedChatId,
    selectedIsVoiceRegistered,
    selectedRegisteredDeviceId,
    connectInfo,
    socketState,
    channel,
    recover,
    evicted,
    endOfDemo,
    attendees,
    exceededUsageLimit,
    connectionResponse,
    evictMySelf,
    micAdvancedOptions,
    remainingMinutes,
    interpretationSpeed,
    setupWebSocket,
    sendMessage,
    dispatchMessage,
    sendMessageIc,
    sendMessageFc,
    sendMessageTss,
    sendMessageText,
    sendMessageMuteGuests,
    setConnectInfo,
    sendMessageConnect,
    sendMessageDisconnect,
    sendMessageAttendees,
    sendMessageEvictUser,
    sendMessageChangeDevice,
    sendMessageChangeLanguage,
    sendMessageAudioStreamStart,
    sendMessageAudioStreamEnd,
    sendMessageVoiceStreamStart,
    sendMessageVoiceStreamEnd,
    sendMessageAudioStreamChunk,
    sendMessageVAD,
    sendMessageEM,
    sendMessageDM,
    close,
    removeAttendeeByUserId,
    addAttendeeByUserId,
    sendMessageFi,
    sendMessageFP,
    findAttendeeNickname,
    findAttendee,
    removeAttendeeAllExcludeMine,
    resetAttendees,
    setAttendees,
    sendMessageEp,
    setExceededUsageLimit,
    setRemainingMinutes,
    setInterpretationSpeed,
  };
});

export const SocketState = {
  CONNECTING: 'CONNECTING',
  OPEN: 'OPEN',
  ERROR_NO_HEADER: 'ERROR_NO_HEADER',
  CLOSED: 'CLOSED',
  RETRY_FAILED: 'RETRY_FAILED',

  isOpen(state) {
    return state === this.OPEN;
  },
  isClosed(state) {
    return state === this.CLOSED;
  },
};
